import isEqual from 'lodash/isEqual';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { cleanFileName, configVars } from 'src/helpers';
import { downloadDocument } from 'src/helpers/downloadUtils';
import { trackPendoEvent } from 'src/helpers/trackPendoEvent';
import { trendAnswerProps, trendEdited } from '../helpers/listCheckHelpers';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag/useFeatureFlag';
import ViewSDKClient from 'src/pdfViewer/ViewSDKClient';
import { FeatureFlagKey } from 'src/ts/enums';
import { IEntry, IFolder, IProof } from 'src/ts/interfaces';
import { useCandidateXray } from '../hooks/useCandidateXray';
import { useFraudTrendEvidence } from '../hooks/useFraudTrendEvidence';
import { useListCheckResolution } from '../hooks/useListCheckResolution';
import { useListCheckRulings } from '../hooks/useListCheckRulings';
import { useViewerModeTime } from '../hooks/useViewerModeTime';
import { useXrayAnalysis } from '../hooks/useXrayAnalysis';
import {
  FormValues,
  getProofData,
  getRulingIcon,
  isUnverifiedOfferLetter,
  OFFER_LETTER_TYPE,
  PENDING_EMPLOYER_VERIFICATION
} from '../helpers/proofHelpers';
import {
  DocumentViewerContainer,
  DocumentViewerWrapper,
  PDFViewerContainer,
  ViewerBody,
  ViewerModeBar,
  RightPanel,
  RightPanelContent,
  DocumentHeader as StyledDocumentHeader,
  DocumentViewer as StyledDocumentViewer,
  WarningBanner
} from '../styles';
import DocumentControls from './DocumentControls';
import DocumentHeader from './DocumentHeader';
import DocumentViewerControls from './DocumentViewerControls';
import ListChecksPanel from './ListChecksPanel';
import ViewerModePill, { ViewerMode } from './ViewerModePill';

interface DocumentViewerProps {
  entry: IEntry;
  folder: IFolder | null;
  proofs: IProof[];
  selectedProofId: string;
  onProofChange: (proofId: string) => void;
  currentProof: IProof | undefined;
  isFirstDocument: boolean;
  isLastDocument: boolean;
  canNavigateBack: boolean;
  canNavigateForward: boolean;
  onNextDocument: () => void;
  onPreviousDocument: () => void;
  entriesInFolder?: IEntry[];
  setSelectedEntryId: (entryId: string) => void;
  assignedEntry?: IEntry | null;
  localFormData?: Record<string, FormValues>;
  onUpdateLocalFormData?: (
    updater: (prev: Record<string, FormValues>) => Record<string, FormValues>
  ) => void;
  onReviewReport: () => void;
  entryProofCache?: Record<string, IProof[]>;
  hideResultControls?: boolean;
  onReclassifyToOfferLetter?: (proofId: string) => void;
  onReclassifyFromOfferLetter?: (proofId: string, newType: IProof['type']) => void;
  onReclassifyCheckSetChange?: (proofId: string, newType: IProof['type']) => Promise<boolean>;
  isPropertyOlvEnabled?: boolean;
  onReportPlatformFeedback?: () => void;
}

const DocumentViewer: FC<DocumentViewerProps> = ({
  entry,
  folder,
  proofs,
  selectedProofId,
  onProofChange,
  currentProof,
  isFirstDocument,
  isLastDocument,
  canNavigateBack,
  canNavigateForward,
  onNextDocument,
  onPreviousDocument,
  entriesInFolder,
  setSelectedEntryId,
  assignedEntry,
  localFormData = {},
  onUpdateLocalFormData,
  onReviewReport,
  entryProofCache,
  hideResultControls,
  onReclassifyToOfferLetter,
  onReclassifyFromOfferLetter,
  onReclassifyCheckSetChange,
  isPropertyOlvEnabled,
  onReportPlatformFeedback
}) => {
  const viewSDKClientRef = useRef<ViewSDKClient | null>(null);
  const isListChecksTabEnabled = useFeatureFlag(FeatureFlagKey.ITL_3258_LIST_CHECKS_TAB);
  const [viewerMode, setViewerMode] = useState<ViewerMode>('document');
  const proofIds = useMemo(() => proofs.map((proof) => proof.id), [proofs]);
  const xray = useXrayAnalysis(proofIds, currentProof?.id, isListChecksTabEnabled);
  const listChecks = useListCheckRulings(currentProof?.id, isListChecksTabEnabled);
  const fraudTrendEvidence = useFraudTrendEvidence(
    currentProof?.id,
    listChecks.data?.rulings ?? null
  );
  const candidateXray = useCandidateXray(currentProof?.id, isListChecksTabEnabled);
  const listCheckResolution = useListCheckResolution();
  const hasListChecks =
    isListChecksTabEnabled && !!listChecks.data && listChecks.data.status !== null;
  const showModeBar = isListChecksTabEnabled;
  const isListChecksActive = hasListChecks && viewerMode === 'list_checks';
  const activeMode: ViewerMode = isListChecksActive ? 'list_checks' : 'document';
  useViewerModeTime(activeMode, showModeBar);
  const trackListCheckAnswer = (
    checkKey: string,
    answer: 'candidate' | 'provider_mismatch' | 'none_of_these' | 'edited' | 'not_edited' | 'undo'
  ) => {
    const prior = listChecks.data?.rulings.find((ruling) => ruling.check_key === checkKey);
    trackPendoEvent('list_check_answered', {
      check_key: checkKey,
      answer,
      prior_ruling: prior?.ruling ?? 'unknown',
      prior_source: prior?.source ?? 'none',
      candidate_count: prior?.candidates.length ?? 0,
      ...trendAnswerProps(prior, trendEdited(answer))
    });
  };
  const savedData = localFormData[currentProof?.id || ''];
  const { result, reason, documentType, olvReasons } = getProofData(currentProof, savedData);
  const { watch, setValue, getValues } = useForm<FormValues>({
    defaultValues: {
      result: result,
      reason: reason,
      documentType: documentType,
      olvReasons: olvReasons
    }
  });

  // Use live watch() values instead of localFormData-derived values.
  // localFormData only updates on save, so passing it to DocumentControls
  // caused stale comparisons (e.g., the OFFER_LETTER Clean-reset logic
  // in handleDocumentTypeChange would compare against outdated values).
  const watchedResult = watch('result');
  const watchedReason = watch('reason');
  const watchedDocumentType = watch('documentType');
  const watchedOlvReasons = watch('olvReasons');

  // OLV action committed: localFormData shows OLV reasons have been set for this proof
  const isOlvActionCommitted = useMemo(() => {
    if (!currentProof) return false;
    const formData = localFormData[currentProof.id];
    if (!formData || formData.documentType !== OFFER_LETTER_TYPE) return false;
    return (
      (formData.olvReasons && formData.olvReasons.length > 0) ||
      formData.reason === PENDING_EMPLOYER_VERIFICATION
    );
  }, [currentProof, localFormData]);

  // Track which proof the form has been initialized for
  const [initializedProofId, setInitializedProofId] = useState('');

  // Auto-save functionality — skip when OLV panel manages results
  // Also skip when form holds stale values from a previous proof (before re-initialization)
  useEffect(() => {
    if (!currentProof || !onUpdateLocalFormData || hideResultControls) return;
    if (currentProof.id !== initializedProofId) return;
    // ITL-2772: skip the auto-save when the proof has transitioned to the
    // auto-insufficient (:UNDETERMINED + [:UNVERIFIED_OFFER_LETTER]) terminal
    // state. The form's react-hook-form values weren't re-initialized for the
    // post-reclassification proof (the init useEffect gates on currentProof.id,
    // which didn't change), so a write here would clobber localFormData with
    // the analyst's pre-reclassification PENDING/PAYSTUB form snapshot and
    // race the handler's affirmative write in ModernizedFraudAnalystReview.
    if (isUnverifiedOfferLetter(currentProof)) return;

    const currentValues = getValues();
    const proofId = currentProof.id;

    onUpdateLocalFormData((prev) => {
      if (prev[proofId] && isEqual(prev[proofId], currentValues)) {
        return prev; // No changes, return previous state
      }
      return {
        ...prev,
        [proofId]: currentValues
      };
    });
  }, [
    watchedResult,
    watchedReason,
    watchedDocumentType,
    watchedOlvReasons,
    currentProof,
    initializedProofId,
    getValues,
    onUpdateLocalFormData,
    hideResultControls
  ]);

  // Always propagate documentType changes to localFormData, even when OLV panel
  // hides result controls. Without this, switching away from OFFER_LETTER while
  // the OLV panel is active wouldn't update effectiveProofType in the parent.
  useEffect(() => {
    if (!currentProof || !onUpdateLocalFormData || !hideResultControls) return;
    if (currentProof.id !== initializedProofId) return;
    if (!watchedDocumentType) return;

    const proofId = currentProof.id;
    onUpdateLocalFormData((prev) => {
      if (prev[proofId]?.documentType === watchedDocumentType) return prev;
      return {
        ...prev,
        [proofId]: { ...(prev[proofId] || getValues()), documentType: watchedDocumentType }
      };
    });
  }, [
    watchedDocumentType,
    currentProof,
    initializedProofId,
    onUpdateLocalFormData,
    hideResultControls,
    getValues
  ]);

  // Form initialization when switching between proofs
  useEffect(() => {
    if (currentProof) {
      const savedData = localFormData[currentProof.id];
      const { result, reason, documentType, olvReasons } = getProofData(currentProof, savedData);

      if (currentProof.is_automatic_review && !savedData) {
        setValue('result', result);
        setValue('documentType', documentType);
        setValue('reason', reason || '');
        setValue('olvReasons', olvReasons);
      } else if (savedData) {
        setValue('result', savedData.result);
        setValue('reason', savedData.reason || '');
        setValue('documentType', savedData.documentType);
        setValue('olvReasons', savedData.olvReasons ?? []);
      } else {
        setValue('result', currentProof.result || 'PENDING');
        setValue('documentType', currentProof.type || '');
        // For existing proofs, load any existing reasons from the database
        if (currentProof.result === 'EDITED' && currentProof.result_edited_reason?.length > 0) {
          setValue(
            'reason',
            currentProof.result_edited_reason.length === 1
              ? currentProof.result_edited_reason[0]
              : currentProof.result_edited_reason
          );
        } else if (
          currentProof.result === 'UNDETERMINED' &&
          currentProof.result_insufficient_reason
        ) {
          setValue('reason', currentProof.result_insufficient_reason);
        } else {
          setValue('reason', '');
        }
        setValue('olvReasons', currentProof.result_insufficient_reasons ?? []);
      }
      setInitializedProofId(currentProof.id);
    }
  }, [currentProof?.id, setValue]);

  const onGeneratePDF = useCallback(
    async (
      file?: string,
      fullName = '',
      fileName = '',
      idContainer = '',
      embedMode = 'SIZED_CONTAINER'
    ) => {
      const viewSDKClient = new ViewSDKClient();
      viewSDKClientRef.current = viewSDKClient;

      await viewSDKClient.ready();
      await viewSDKClient.previewFile(
        idContainer,
        {
          embedMode
        },
        file,
        cleanFileName(fullName + '_' + fileName),
        selectedProofId,
        configVars.adobe.pdf_token
      );
    },
    [selectedProofId]
  );

  // Generate PDF when document changes
  useEffect(() => {
    if (folder && entry && proofs && currentProof) {
      onGeneratePDF(
        currentProof.file,
        entry?.applicant?.full_name,
        currentProof.fileName,
        'pdf-viewer-container',
        'SIZED_CONTAINER'
      );
    }
  }, [folder, entry, proofs, currentProof, selectedProofId, onGeneratePDF]);

  const handleZoomIn = async () => {
    if (viewSDKClientRef.current) {
      try {
        await viewSDKClientRef.current.zoomIn();
      } catch (error) {
        console.error('Error zooming in:', error);
      }
    }
  };

  const handleZoomOut = async () => {
    if (viewSDKClientRef.current) {
      try {
        await viewSDKClientRef.current.zoomOut();
      } catch (error) {
        console.error('Error zooming out:', error);
      }
    }
  };

  const handleGetRulingIcon = (proof: IProof) => {
    return getRulingIcon(proof, localFormData);
  };

  const handleDownload = async () => {
    if (!currentProof?.file) return;
    await downloadDocument(currentProof.file, currentProof.fileName, currentProof.id);
  };

  return (
    <RightPanel>
      <RightPanelContent>
        <DocumentHeader
          entry={entry}
          proofs={proofs}
          selectedProofId={selectedProofId}
          onProofChange={onProofChange}
          currentProof={currentProof}
          getRulingIcon={handleGetRulingIcon}
          entriesInFolder={entriesInFolder}
          setSelectedEntryId={setSelectedEntryId}
          assignedEntry={assignedEntry}
          entryProofCache={entryProofCache}
          onReportPlatformFeedback={onReportPlatformFeedback}
        />

        <StyledDocumentHeader style={{ display: 'none' }} />

        <StyledDocumentViewer $hasModeBar={showModeBar}>
          {assignedEntry && entry.id !== assignedEntry.id && (
            <WarningBanner>
              This is not the assigned submission. You are viewing an older, merged version.
            </WarningBanner>
          )}
          {currentProof && (
            <DocumentViewerWrapper>
              {showModeBar && (
                <ViewerModeBar>
                  <ViewerModePill
                    mode={activeMode}
                    showXray={false}
                    showListChecks={hasListChecks}
                    onChange={setViewerMode}
                  />
                </ViewerModeBar>
              )}
              <ViewerBody>
                <DocumentViewerContainer>
                  {/* The List checks panel overlays the viewer instead of replacing it, so the
                      Adobe viewer never unmounts and keeps its zoom and page. */}
                  <PDFViewerContainer id="pdf-viewer-container" />
                </DocumentViewerContainer>
                {isListChecksActive && (
                  <ListChecksPanel
                    key={currentProof.id}
                    documentXray={xray}
                    candidateXray={candidateXray}
                    fraudTrendEvidence={fraudTrendEvidence}
                    status={listChecks.status}
                    data={listChecks.data}
                    error={listChecks.error}
                    onRetry={listChecks.retry}
                    currentProof={currentProof}
                    isSubmitting={listCheckResolution.isSubmitting}
                    onSelect={async (checkKey, rowId) => {
                      const resolved = await listCheckResolution.resolve(
                        currentProof.id,
                        checkKey,
                        { resolution: 'candidate', row_id: rowId }
                      );
                      if (resolved) {
                        trackListCheckAnswer(checkKey, 'candidate');
                        listChecks.applyEntry(currentProof.id, resolved);
                      }
                    }}
                    onProviderMismatch={async (checkKey, rowId, note) => {
                      const resolved = await listCheckResolution.resolve(
                        currentProof.id,
                        checkKey,
                        { resolution: 'provider_mismatch', row_id: rowId, ...(note && { note }) }
                      );
                      if (resolved) {
                        trackListCheckAnswer(checkKey, 'provider_mismatch');
                        listChecks.applyEntry(currentProof.id, resolved);
                      }
                    }}
                    onNoneOfThese={async (checkKey, note) => {
                      const resolved = await listCheckResolution.resolve(
                        currentProof.id,
                        checkKey,
                        { resolution: 'none_of_these', ...(note && { note }) }
                      );
                      if (resolved) {
                        trackListCheckAnswer(checkKey, 'none_of_these');
                        listChecks.applyEntry(currentProof.id, resolved);
                      }
                    }}
                    onFraudTrendAnswer={async (checkKey, edited, note) => {
                      const resolution = edited ? 'edited' : 'not_edited';
                      const resolved = await listCheckResolution.resolve(
                        currentProof.id,
                        checkKey,
                        { resolution, ...(note && { note }) }
                      );
                      if (resolved) {
                        trackListCheckAnswer(checkKey, resolution);
                        listChecks.applyEntry(currentProof.id, resolved);
                      }
                    }}
                    onUndo={async (checkKey) => {
                      const restored = await listCheckResolution.undo(currentProof.id, checkKey);
                      if (restored) {
                        trackListCheckAnswer(checkKey, 'undo');
                        listChecks.applyEntry(currentProof.id, restored);
                      }
                    }}
                  />
                )}
                {!isListChecksActive && (
                  <DocumentViewerControls
                    onZoomIn={handleZoomIn}
                    onZoomOut={handleZoomOut}
                    onDownload={handleDownload}
                  />
                )}
              </ViewerBody>
            </DocumentViewerWrapper>
          )}
        </StyledDocumentViewer>

        <DocumentControls
          currentProof={currentProof}
          selectedProofId={selectedProofId}
          watchedResult={watchedResult}
          watchedReason={watchedReason}
          watchedDocumentType={watchedDocumentType}
          watchedOlvReasons={watchedOlvReasons ?? []}
          setValue={setValue}
          isFirstDocument={isFirstDocument}
          isLastDocument={isLastDocument}
          canNavigateBack={canNavigateBack}
          canNavigateForward={canNavigateForward}
          onNextDocument={onNextDocument}
          onPreviousDocument={onPreviousDocument}
          onReviewReport={onReviewReport}
          proofs={proofs}
          localFormData={localFormData}
          hideResultControls={hideResultControls}
          isOlvActionCommitted={isOlvActionCommitted}
          onReclassifyToOfferLetter={onReclassifyToOfferLetter}
          onReclassifyFromOfferLetter={onReclassifyFromOfferLetter}
          onReclassifyCheckSetChange={onReclassifyCheckSetChange}
          isPropertyOlvEnabled={isPropertyOlvEnabled}
        />
      </RightPanelContent>
    </RightPanel>
  );
};

export default DocumentViewer;
